import { useMemo } from "react";
import { useLazyLoadQuery, graphql } from "react-relay";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { aiDateRanges } from "../../../../pages/Insights";
import { useGlobalState } from "../../../../state/v2/store";
import { useInsightsFilterMenuParams } from "../FilterMenu/useFilterMenuParams";
import { AISummaryInsightQuery, KPI } from "./__generated__/AISummaryInsightQuery.graphql";

interface AISummaryProps {
  evaluationType: AISummaryInsightQuery["variables"]["input"]["evaluationType"];
}
function renderMarkdown(content: string): string {
  const sections = content.split(/\n\s*\n/);

  const groups: string[] = [];
  let intro = '';

  sections.forEach((section) => {
    const headerMatch = section.match(/^\*\*(.*?)\*\*/);
    if (headerMatch) {
      const header = `<strong>${headerMatch[1]}</strong>`;
      const bullets = section
        .split('\n')
        .slice(1)
        .filter(line => line.trim().startsWith('- '))
        .map(line => `<li>${line.trim().substring(2)}</li>`)
        .join('');

      const groupHtml = `
        <div class="mb-6">
          <p class="font-semibold mb-2 text-left text-xl">${header}</p>
          <ul class="list-disc text-left" style="padding-left: 20px;">
            ${bullets}
          </ul>
        </div>
      `;

      groups.push(groupHtml);
    } else if (!intro) {
      // Take first paragraph with no header as the intro
      intro = `<p class="mb-6">${section
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>')}</p>`;
    }
  });

  const firstCol = groups.slice(0, 2).join('');
  const secondCol = groups.slice(2).join('');

  return `
    ${intro}
    <div class="flex justify-center mt-6 gap-12">
      <div class="w-1/2">${firstCol}</div>
      <div class="w-1/2">${secondCol}</div>
    </div>
  `;
}

export const AISummary = ({ evaluationType }: AISummaryProps) => {
  const [{ insightKpi, aiDateRange }] = useInsightsFilterMenuParams();

  const brandId = useGlobalState((b) =>
    b.brand?.map((brand) => brand.id).join(",")
  );

  const variables = useMemo<AISummaryInsightQuery["variables"]>(() => ({
    skip: !brandId,
    input: {
      brandId: brandId!,
      kpi: insightKpi as KPI,
      aiDateRange: aiDateRange || aiDateRanges[0].value,
      evaluationType,
    },
  }), [aiDateRange, insightKpi, brandId, evaluationType]);

  const data = useLazyLoadQuery<AISummaryInsightQuery>(
    graphql`
      query AISummaryInsightQuery($input: GetAISummaryRequest!, $skip: Boolean!) {
        getAISummary(input: $input) @skip(if: $skip) {
          content
        }
      }
    `,
    variables,
    { fetchPolicy: "network-only" }
  );

  // Use `dangerouslySetInnerHTML` to inject the HTML string into the DOM
  const htmlContent = data.getAISummary?.content ? (
    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(data.getAISummary.content) }} />
  ) : null;
  
  return (
    <div className="flex-1">
      {(!data.getAISummary || !data.getAISummary.content) ? (
        <EmptyState message="It looks like we don't have any relevant data for your filters." />
      ) : (
        <div>
          <h5 className="text-2xl">
            {evaluationType === "TOP_PERFORMING" ? "Top" : "Worse"} Performing Themes
          </h5>
          {htmlContent}
        </div>
      )}
    </div>
  );  
};

export default AISummary;
